import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import {
  CalendarDaysIcon,
  MapPinIcon,
  UserGroupIcon,
  ChevronRightIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';

const EventCategories = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Event categories with counts
  const categories = [
    { id: 'all', name: 'All Events', count: 45, color: 'gray' },
    { id: 'medical', name: 'Medical Training', count: 12, color: 'blue' },
    { id: 'conference', name: 'Conferences', count: 8, color: 'purple' },
    { id: 'holiday', name: 'Holidays', count: 6, color: 'red' },
    { id: 'meeting', name: 'Department Meetings', count: 15, color: 'green' },
    { id: 'workshop', name: 'Workshops', count: 4, color: 'orange' },
  ];

  // Sample events by category
  const eventsByCategory = {
    medical: [
      {
        id: 1,
        title: "Advanced Cardiac Surgery Techniques",
        date: "2024-02-15",
        location: "Apollo Medical Center",
        attendees: 45,
        category: "Medical Training",
        color: "blue"
      },
      {
        id: 2,
        title: "Emergency Medicine Protocol Update",
        date: "2024-02-20",
        location: "Training Hall A",
        attendees: 60,
        category: "Medical Training",
        color: "blue"
      }
    ],
    conference: [
      {
        id: 3,
        title: "International Healthcare Conference 2024",
        date: "2024-03-10",
        location: "Main Auditorium",
        attendees: 200,
        category: "Conference",
        color: "purple"
      },
      {
        id: 4,
        title: "Digital Health Innovation Summit",
        date: "2024-03-25",
        location: "Convention Center",
        attendees: 150,
        category: "Conference",
        color: "purple"
      }
    ],
    holiday: [
      {
        id: 5,
        title: "Independence Day Celebration",
        date: "2024-08-15",
        location: "Apollo Main Campus",
        attendees: 500,
        category: "National Holiday",
        color: "red"
      },
      {
        id: 6,
        title: "Diwali Festival Celebration",
        date: "2024-11-01",
        location: "Main Campus Ground",
        attendees: 600,
        category: "Festival",
        color: "red"
      }
    ],
    meeting: [
      {
        id: 7,
        title: "Monthly Department Heads Meeting",
        date: "2024-02-05",
        location: "Conference Room 1",
        attendees: 25,
        category: "Department Meeting",
        color: "green"
      },
      {
        id: 8,
        title: "Nursing Staff Coordination Meeting",
        date: "2024-02-12",
        location: "Nursing Station Hall",
        attendees: 40,
        category: "Department Meeting",
        color: "green"
      }
    ],
    workshop: [
      {
        id: 9,
        title: "Patient Care Excellence Workshop",
        date: "2024-02-28",
        location: "Training Center",
        attendees: 30,
        category: "Workshop",
        color: "orange"
      },
      {
        id: 10,
        title: "Technology Integration Workshop",
        date: "2024-03-05",
        location: "IT Lab",
        attendees: 20,
        category: "Workshop",
        color: "orange"
      }
    ]
  };

  const getColorClasses = (color) => {
    const colorMap = {
      gray: 'bg-gray-100 text-gray-800 border-gray-300',
      blue: 'bg-blue-50 text-blue-800 border-blue-200',
      purple: 'bg-purple-50 text-purple-800 border-purple-200',
      red: 'bg-red-50 text-red-800 border-red-200',
      green: 'bg-green-50 text-green-800 border-green-200',
      orange: 'bg-orange-50 text-orange-800 border-orange-200',
    };
    return colorMap[color] || 'bg-gray-50 text-gray-800 border-gray-200';
  };

  const getButtonColorClasses = (color, isSelected) => {
    if (isSelected) {
      const selectedMap = {
        gray: 'bg-apollo-accent text-white border-apollo-accent',
        blue: 'bg-blue-600 text-white border-blue-600',
        purple: 'bg-purple-600 text-white border-purple-600',
        red: 'bg-red-600 text-white border-red-600',
        green: 'bg-green-600 text-white border-green-600',
        orange: 'bg-orange-600 text-white border-orange-600',
      };
      return selectedMap[color] || 'bg-apollo-accent text-white border-apollo-accent';
    } else {
      return 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50';
    }
  };

  const getCurrentEvents = () => {
    if (selectedCategory === 'all') {
      return Object.values(eventsByCategory).flat();
    }
    return eventsByCategory[selectedCategory] || [];
  };

  const filteredEvents = getCurrentEvents().filter(event =>
    event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Component */}
      <Header />

      {/* Event Categories Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Event Categories</h1>
              <p className="text-gray-600 mt-1 text-sm sm:text-base">Browse events by category</p>
            </div>
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="relative flex-1 sm:flex-none">
                <input
                  type="text"
                  placeholder="Search events..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full sm:w-auto pl-8 sm:pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-apollo-accent focus:border-transparent text-sm sm:text-base"
                />
                <FunnelIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-2 sm:left-3 top-2.5" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sub Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-4 sm:space-x-8 overflow-x-auto">
            <button 
              onClick={() => navigate('/events')}
              className="border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 py-4 px-1 text-sm font-medium transition-colors whitespace-nowrap"
            >
              All Events
            </button>
            <button 
              onClick={() => navigate('/events/categories')}
              className="border-b-2 border-apollo-accent text-apollo-accent py-4 px-1 text-sm font-medium whitespace-nowrap"
            >
              Categories
            </button>
            <button 
              onClick={() => navigate('/events/my-events')}
              className="border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 py-4 px-1 text-sm font-medium transition-colors whitespace-nowrap"
            >
              My Events
            </button>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Category Filters */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-3 sm:px-4 py-2 rounded-lg border font-medium transition-all duration-200 text-sm sm:text-base ${
                  getButtonColorClasses(category.color, selectedCategory === category.id)
                }`}
              >
                {category.name}
                <span className="ml-1 sm:ml-2 text-xs sm:text-sm opacity-75">({category.count})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Category Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
          {categories.slice(1).map(category => (
            <div
              key={category.id}
              className={`p-3 sm:p-4 md:p-6 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-md ${
                selectedCategory === category.id 
                  ? getColorClasses(category.color)
                  : 'bg-white border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => setSelectedCategory(category.id)}
            >
              <div className="text-center">
                <div className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">{category.count}</div>
                <div className="text-xs sm:text-sm font-medium text-gray-600">{category.name}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Events List */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
              {selectedCategory === 'all' 
                ? `All Events (${filteredEvents.length})` 
                : `${categories.find(c => c.id === selectedCategory)?.name} (${filteredEvents.length})`
              }
            </h2>
          </div>

          {filteredEvents.length === 0 ? (
            <div className="px-4 sm:px-6 py-8 sm:py-12 text-center">
              <CalendarDaysIcon className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No Events Found</h3>
              <p className="text-sm sm:text-base text-gray-600">Try adjusting your search or select a different category.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredEvents.map(event => (
                <div key={event.id} className="px-4 sm:px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer group">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 group-hover:text-apollo-accent transition-colors mb-2">
                        {event.title}
                      </h3>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-6 space-y-2 sm:space-y-0 text-sm text-gray-600">
                        <div className="flex items-center space-x-2">
                          <CalendarDaysIcon className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{new Date(event.date).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <MapPinIcon className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{event.location}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <UserGroupIcon className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{event.attendees} attendees</span>
                        </div>
                      </div>
                      <div className="mt-3">
                        <span className={`inline-block px-2 sm:px-3 py-1 rounded-full text-xs font-medium border ${getColorClasses(event.color)}`}>
                          {event.category}
                        </span>
                      </div>
                    </div>
                    <ChevronRightIcon className="w-5 h-5 text-gray-400 group-hover:text-apollo-accent transition-colors ml-2 flex-shrink-0" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Load More Button */}
        {filteredEvents.length > 0 && (
          <div className="text-center mt-6 sm:mt-8">
            <button className="px-4 sm:px-6 py-2 sm:py-3 bg-apollo-accent text-white rounded-lg hover:bg-apollo-primary transition-colors font-medium text-sm sm:text-base">
              Load More Events
            </button>
          </div>
        )}
      </div>

      {/* Footer Component */}
      <Footer />
    </div>
  );
};

export default EventCategories;
